import { locateRow } from "../find";
import { createGroup } from "../factories";
import { isRow } from "../sections";
import type { EmailDocument, Row } from "../types";
import type { EditorDraft } from "./types";

export function resolveGroupableTopLevelRows(
  document: EmailDocument,
  rowIds: string[],
): { insertIndex: number; rows: Row[] } | null {
  if (rowIds.length < 2 || new Set(rowIds).size !== rowIds.length) {
    return null;
  }

  const rowIdSet = new Set(rowIds);
  const matched: Array<{ index: number; row: Row }> = [];
  for (let index = 0; index < document.sections.length; index += 1) {
    const section = document.sections[index];
    if (!isRow(section) || !rowIdSet.has(section.id)) {
      continue;
    }
    matched.push({ index, row: section });
  }

  if (matched.length !== rowIds.length) {
    return null;
  }

  const indices = matched.map(entry => entry.index).sort((a, b) => a - b);
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] !== indices[i - 1] + 1) {
      return null;
    }
  }

  const rows = matched
    .sort((a, b) => a.index - b.index)
    .map(entry => entry.row);

  return { insertIndex: indices[0], rows };
}

export function canGroupRows(document: EmailDocument, rowIds: string[]): boolean {
  return resolveGroupableTopLevelRows(document, rowIds) !== null;
}

export function groupRows(draft: EditorDraft, rowIds: string[]): boolean {
  const resolved = resolveGroupableTopLevelRows(draft.document, rowIds);
  if (!resolved) {
    return false;
  }

  const { insertIndex, rows } = resolved;
  const indices: number[] = [];
  for (const row of rows) {
    const located = locateRow(draft.document.sections, row.id);
    if (located?.location.parent === "document") {
      indices.push(located.location.sectionIndex);
    }
  }
  indices.sort((a, b) => b - a);

  for (const index of indices) {
    draft.document.sections.splice(index, 1);
  }

  const group = createGroup(rows);
  draft.document.sections.splice(insertIndex, 0, group);
  draft.selection = { kind: "group", groupId: group.id };
  draft.selectedRowIds = [];
  draft.primaryRowId = null;
  draft.anchorRowId = null;
  return true;
}
